import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/assets";
import { ytThumb } from "@/lib/stemVideos";
import { LogOut, Trash2, Plus, ArrowLeft, PlayCircle, Youtube } from "lucide-react";

const CATEGORIES = [
  { key: "sciences", label: "Sciences" },
  { key: "tech", label: "Technologie & Ingénierie (STEM)" },
  { key: "maths", label: "Mathématiques" },
  { key: "langues", label: "Langues" },
];
const catLabel = (k) => (CATEGORIES.find((c) => c.key === k)?.label || k);

const extractId = (url) => {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (m) return m[1];
  return /^[A-Za-z0-9_-]{11}$/.test((url || "").trim()) ? url.trim() : null;
};

export default function AdminLibrary() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ title: "", youtube_url: "", category: "sciences", author: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/stem-videos"); setVideos(r.data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const previewId = extractId(form.youtube_url);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Le titre est requis");
    if (!extractId(form.youtube_url)) return toast.error("Lien YouTube invalide");
    setSaving(true);
    try {
      await api.post("/admin/stem-videos", form);
      toast.success("Vidéo ajoutée à la bibliothèque !");
      setForm({ title: "", youtube_url: "", category: form.category, author: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/stem-videos/${id}`); toast.success("Vidéo supprimée"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };
  const inp = "w-full h-12 px-3 border border-icdh-navy/20 focus:border-icdh-red outline-none bg-white text-icdh-navy";

  return (
    <div className="min-h-screen bg-icdh-surface text-icdh-navy">
      <header className="bg-icdh-navy text-icdh-white sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="ICDH" className="h-9 w-9 object-contain bg-white rounded-sm p-0.5" />
            <span className="font-extrabold tracking-tight">ICDH · Administration de la Bibliothèque</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/admin" data-testid="lib-back-dashboard" className="flex items-center gap-2 hover:text-icdh-red"><ArrowLeft size={16} />Tableau de bord</Link>
            <a href="/stem" target="_blank" rel="noreferrer" data-testid="lib-view-stem" className="flex items-center gap-2 hover:text-icdh-red"><PlayCircle size={16} />Voir la bibliothèque</a>
            <button onClick={doLogout} data-testid="lib-logout" className="flex items-center gap-2 hover:text-icdh-red"><LogOut size={16} />Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-10 grid lg:grid-cols-5 gap-10">
        {/* Add form */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-icdh-navy/10 p-6 md:p-8 sticky top-24">
            <div className="flex items-center gap-3 text-icdh-red">
              <Plus size={22} /><h2 className="font-serif text-2xl text-icdh-navy">Ajouter une Vidéo</h2>
            </div>
            <p className="mt-2 text-sm text-icdh-navy/60">Mettez votre vidéo sur YouTube, puis collez le lien ici. Elle apparaîtra instantanément dans la bibliothèque des élèves.</p>
            <form onSubmit={submit} data-testid="add-video-form" className="mt-6 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-icdh-navy/60">Titre de la vidéo</label>
                <input className={`${inp} mt-2`} data-testid="video-title" placeholder="Ex : Les fables de La Fontaine" value={form.title} onChange={set("title")} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-icdh-navy/60">Lien YouTube</label>
                <div className="relative mt-2">
                  <Youtube size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-icdh-red" />
                  <input className={`${inp} pl-10`} data-testid="video-url" placeholder="https://www.youtube.com/watch?v=..." value={form.youtube_url} onChange={set("youtube_url")} />
                </div>
              </div>
              {previewId && (
                <div data-testid="video-preview" className="overflow-hidden border border-icdh-navy/10">
                  <img src={ytThumb(previewId)} alt="aperçu" className="w-full aspect-video object-cover" />
                </div>
              )}
              <div className="grid grid-cols-1 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.16em] text-icdh-navy/60">Matière</label>
                  <select className={`${inp} mt-2`} data-testid="video-category" value={form.category} onChange={set("category")}>
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.16em] text-icdh-navy/60">Source (optionnel)</label>
                  <input className={`${inp} mt-2`} data-testid="video-author" placeholder="Ex : ICDH, TED-Ed…" value={form.author} onChange={set("author")} />
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="submit-video"
                className="w-full h-14 bg-icdh-red text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors disabled:opacity-60">
                {saving ? "..." : "Ajouter la Vidéo"}
              </button>
            </form>
          </div>
        </div>

        {/* Video list */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-2xl">Vidéos publiées <span className="text-icdh-navy/40">({videos.length})</span></h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {videos.length === 0 && <p className="text-icdh-navy/50">Aucune vidéo pour le moment.</p>}
            {videos.map((v) => (
              <div key={v.id} data-testid={`lib-video-${v.id}`} className="group bg-white border border-icdh-navy/10 overflow-hidden">
                <div className="relative aspect-video overflow-hidden">
                  <img src={ytThumb(v.video_id)} alt={v.title} className="w-full h-full object-cover" />
                  <button onClick={() => remove(v.id)} data-testid={`del-video-${v.id}`}
                    className="absolute top-2 right-2 h-9 w-9 bg-icdh-red text-white flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"><Trash2 size={16} /></button>
                </div>
                <div className="p-4">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-icdh-red">{catLabel(v.category)}</span>
                  <h4 className="mt-1 font-sans font-bold text-icdh-navy leading-snug">{v.title}</h4>
                  {v.author && <p className="text-sm text-icdh-navy/50 mt-1">{v.author}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
